import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import { MenuItem } from '@material-ui/core'
import { FormikErrors, FormikTouched } from 'formik'
import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import SectionTitle from '../../components/SectionTitle'
import { WALLETS_BRIEF_QUERY } from '../../graphql/wallet'
import { REFERENCE_TIMES_QUERY } from '../../graphql/walletPaymentTerm'
import { getTranslate } from '../../localization'
import { WalletBrief } from '../../types/wallet'
import { ReferenceTime } from '../../types/walletEnums'
import { PaymentTerm } from '../../types/walletPaymentTerm'
import TimeDurationInput from './TimeDurationInput'

interface PaymentTermDetailsProps {
  values: PaymentTerm
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<PaymentTerm>
  touched: FormikTouched<PaymentTerm>
  handleBlur: any
  isSubmitting: any
  classes: any
  loading: boolean
  initialValues: PaymentTerm
}

export const PaymentTermDetails: React.FC<PaymentTermDetailsProps> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  classes,
  loading,
  initialValues
}) => {
  const params = useParams<{ id: string }>()

  const { data: referenceTimes, loading: loadingForReferenceTimes } = useQuery<{
    referenceTimes: ReferenceTime[]
  }>(REFERENCE_TIMES_QUERY)
  const [getWallets] = useLazyQuery<{
    walletsBrief: WalletBrief[]
  }>(WALLETS_BRIEF_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    getWallets()
  }, [])

  return (
    <>
      <div className={classes.titleContainer}>
        <SectionTitle title="Details" marginBottom />
      </div>{' '}
      <div className={classes.fieldsContainer}>
        <InputWithLabel
          data-cy="paymentTermName"
          name="name"
          label={getTranslate('Name')}
          placeholder={getTranslate('Enter the payment Term name')}
          autoComplete="off"
          value={values?.name}
          onChange={(event) => setFieldValue('name', event.currentTarget.value)}
          error={(touched.name && !!errors.name) || undefined}
          helpertext={
            touched.name ? getTranslate(errors.name || '') : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          data-cy="paymentTermCode"
          name="code"
          label={getTranslate('Code')}
          placeholder={getTranslate('Enter the payment Term Code')}
          autoComplete="off"
          value={values?.displayPaymentTermCode}
          onChange={(event) => {
            setFieldValue('code', event.currentTarget.value)
            setFieldValue('displayPaymentTermCode', event.currentTarget.value)
          }}
          error={(touched.code && !!errors.code) || undefined}
          helpertext={
            touched.code ? getTranslate(errors.code || '') : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          data-cy="paymentTermGracePeriodDays"
          name="gracePeriodDays"
          label={getTranslate('Grace Period Days')}
          placeholder={getTranslate('Enter grace Period Days for paymentTerm')}
          autoComplete="off"
          value={values?.gracePeriodDays}
          onChange={(event) =>
            setFieldValue(
              'gracePeriodDays',
              parseInt(event.currentTarget?.value, 10)
            )
          }
          error={
            (touched.gracePeriodDays && !!errors.gracePeriodDays) || undefined
          }
          helpertext={
            touched.gracePeriodDays
              ? getTranslate(errors.gracePeriodDays || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          type="number"
          inputSize="small"
        />
        <InputWithLabel
          name="penaltyAdministrationFee"
          label={getTranslate('Penalty Administration Fee')}
          placeholder={getTranslate(
            'Enter Penalty Administration Fee for paymentTerm'
          )}
          autoComplete="off"
          value={values?.penaltyAdministrationFee}
          onChange={(event) =>
            setFieldValue('penaltyAdministrationFee', event.currentTarget.value)
          }
          error={
            (touched.penaltyAdministrationFee &&
              !!errors.penaltyAdministrationFee) ||
            undefined
          }
          helpertext={
            touched.penaltyAdministrationFee
              ? getTranslate(errors.penaltyAdministrationFee || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          type="number"
        />
        <SelectWithLabel
          data-cy="paymentTermPaymentTermType"
          disabled={!!params.id}
          name="referenceTime"
          id="referenceTime"
          fullWidth
          value={values?.referenceTime ? values?.referenceTime : ''}
          onChange={(event) => {
            setFieldValue('referenceTime', event.target.value)
          }}
          size="small"
          error={(touched.referenceTime && !!errors.referenceTime) || undefined}
          helpertext={
            touched.referenceTime
              ? getTranslate(errors.referenceTime || '')
              : undefined
          }
          label={getTranslate('Reference Type')}>
          {!loadingForReferenceTimes &&
          referenceTimes &&
          referenceTimes?.referenceTimes ? (
            referenceTimes.referenceTimes.map((referenceTime) => (
              <MenuItem key={referenceTime} value={referenceTime}>
                {referenceTime}
              </MenuItem>
            ))
          ) : (
            <MenuItem key={'loading ...'} value={'loading ...'}>
              {getTranslate('loading ...')}
            </MenuItem>
          )}
        </SelectWithLabel>
        <InputWithLabel
          name="earlyPaymentDiscountPercentage"
          label={getTranslate('Early Payment Discount Percentage')}
          placeholder={getTranslate(
            'Enter Early Payment Discount Percentage for paymentTerm'
          )}
          autoComplete="off"
          value={values?.earlyPaymentDiscountPercentage}
          onChange={(event) =>
            setFieldValue(
              'earlyPaymentDiscountPercentage',
              event.currentTarget.value
            )
          }
          error={
            (touched.earlyPaymentDiscountPercentage &&
              !!errors.earlyPaymentDiscountPercentage) ||
            undefined
          }
          helpertext={
            touched.earlyPaymentDiscountPercentage
              ? getTranslate(errors.earlyPaymentDiscountPercentage || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          type="number"
        />

        <TimeDurationInput
          initialValues={initialValues.proformaExpirationDuration}
          loading={loading}
          onTimeChange={(formattedTime: string) => {
            // Set the value of 'proformaExpirationDuration' in Formik form or any other state
            setFieldValue('proformaExpirationDuration', formattedTime)
          }} // This callback will set the formatted time string
          label={getTranslate('Proforma Expiration Duration')}
        />
        <TimeDurationInput
          initialValues={initialValues.proformaPaymentDeadlineDuration}
          loading={loading}
          onTimeChange={(formattedTime: string) => {
            // Set the value of 'proformaPaymentDeadlineDuration' in Formik form or any other state
            setFieldValue('proformaPaymentDeadlineDuration', formattedTime)
          }} // This callback will set the formatted time string
          label={getTranslate('proforma Payment Deadline Duration')}
        />
        {/* I want to use the new component here. i want it to be reusable when not using formik too */}
      </div>
    </>
  )
}
